import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { formatDateContext, parseDateContext } from '../../../util/searchDates';
import {
  isWeddingDatePromptDone,
  markWeddingDatePromptDone,
  readStoredWeddingDate,
} from '../../../util/weddingDate';
import { H4, IconCancel } from '../../../components';

import DayOrMonthPicker from '../BookingDateRangeFilter/DayOrMonthPicker';

import css from './WeddingDatePrompt.module.css';

const TITLE_ID = 'WeddingDatePrompt.title';
const KEY_CODE_ESCAPE = 27;

/**
 * Asks a couple once for their wedding date, above the results of an undated search. It stays
 * away for good once a date is saved or the prompt is closed.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.hasDate whether the search already asks about a date
 * @param {Function} props.onPick called with { dates, dateMode } for the picked day or month
 * @returns {JSX.Element|null}
 */
const WeddingDatePrompt = props => {
  const { hasDate, onPick } = props;
  const intl = useIntl();
  const [isShown, setIsShown] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickedLabel, setPickedLabel] = useState(null);
  const rootRef = useRef(null);
  const toggleRef = useRef(null);
  const confirmationRef = useRef(null);
  const focusAfterCloseRef = useRef(null);

  // Storage is only readable after mount, so the server never renders the prompt.
  useEffect(() => {
    setIsShown(!readStoredWeddingDate() && !isWeddingDatePromptDone());
  }, []);

  // The picker the focus was in is gone, so focus moves to the sentence that replaced it.
  useEffect(() => {
    if (pickedLabel) {
      confirmationRef.current?.focus();
    }
  }, [pickedLabel]);

  // Closing removes the button that had focus, so focus moves on to the first result.
  useEffect(() => {
    if (!isShown && focusAfterCloseRef.current) {
      focusAfterCloseRef.current.focus();
      focusAfterCloseRef.current = null;
    }
  }, [isShown]);

  if (!isShown || (hasDate && !pickedLabel)) {
    return null;
  }

  const close = () => {
    markWeddingDatePromptDone();
    if (rootRef.current?.contains(document.activeElement)) {
      focusAfterCloseRef.current = rootRef.current.nextElementSibling?.querySelector(
        'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
      );
    }
    setIsShown(false);
  };

  const handleChange = next => {
    if (!next.dates) {
      close();
      return;
    }
    onPick(next);
    setPickedLabel(formatDateContext(parseDateContext(next.dates, next.dateMode), intl));
  };

  return (
    <section className={css.root} aria-labelledby={TITLE_ID} ref={rootRef}>
      <H4 as="h2" id={TITLE_ID} className={css.title}>
        <FormattedMessage id="WeddingDatePrompt.title" />
      </H4>
      {pickedLabel ? (
        <p className={css.text} ref={confirmationRef} tabIndex={-1}>
          <FormattedMessage id="WeddingDatePrompt.saved" values={{ date: pickedLabel }} />
        </p>
      ) : (
        <>
          <p className={css.text}>
            <FormattedMessage id="WeddingDatePrompt.text" />
          </p>
          <button
            type="button"
            className={css.toggle}
            ref={toggleRef}
            aria-expanded={isPickerOpen}
            onClick={() => setIsPickerOpen(open => !open)}
          >
            <FormattedMessage id="WeddingDatePrompt.choose" />
          </button>
          {isPickerOpen ? (
            <div
              className={css.picker}
              onKeyDown={e => {
                if (e.keyCode === KEY_CODE_ESCAPE) {
                  setIsPickerOpen(false);
                  toggleRef.current?.focus();
                }
              }}
            >
              <DayOrMonthPicker
                value={null}
                dateMode={null}
                dayInputId="WeddingDatePrompt.day"
                onChange={handleChange}
              />
            </div>
          ) : null}
        </>
      )}
      <button
        type="button"
        className={css.close}
        onClick={close}
        aria-label={intl.formatMessage({ id: 'WeddingDatePrompt.close' })}
      >
        <IconCancel />
      </button>
    </section>
  );
};

export default WeddingDatePrompt;
